import { SerializedTableCellNode, TableCellHeaderStates, TableCellNode } from "@lexical/table";
import {
  $applyNodeReplacement,
  $createParagraphNode,
  $isInlineElementOrDecoratorNode,
  $isLineBreakNode,
  $isTextNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  ParagraphNode,
  Spread
} from "lexical";

const CUSTOM_COLUMN_WIDTH = 62;

export type SerializedCustomTableCellNode = Spread<
  {
    backgroundColor?: string | null;
    textColor?: string | null;
    fontSize?: string | null;
    textAlign?: "left" | "center" | "right" | null;
    fontWeight?: "normal" | "bold" | null;
    fontStyle?: "normal" | "italic" | null;
    textDecoration?: "none" | "underline" | null;
    borderStyle?: string | null;
    padding?: string | null;
    height?: number | null;
  },
  SerializedTableCellNode
>;

export class CustomTableCellNode extends TableCellNode {
  __textColor: string | null = null;
  __fontSize: string | null = null;
  __textAlign: "left" | "center" | "right" | null = null;
  __fontWeight: "normal" | "bold" | null = null;
  __fontStyle: "normal" | "italic" | null = null;
  __textDecoration: "none" | "underline" | null = null;
  __borderStyle: string | null = null;
  __padding: string | null = null;
  __height: number | null = null;

  constructor(
    headerState = 0,
    colSpan?: number,
    width?: number,
    key?: NodeKey
  ) {
    super(headerState, colSpan, width, key);
  }

  static getType(): string {
    return "custom-tablecell";
  }

  static clone(node: CustomTableCellNode): CustomTableCellNode {
    const cellNode = new CustomTableCellNode(
      node.__headerState,
      node.__colSpan,
      node.__width,
      node.__key
    );
    cellNode.__rowSpan = node.__rowSpan;
    cellNode.__backgroundColor = node.__backgroundColor;
    cellNode.__textColor = node.__textColor;
    cellNode.__fontSize = node.__fontSize;
    cellNode.__textAlign = node.__textAlign;
    cellNode.__fontWeight = node.__fontWeight;
    cellNode.__fontStyle = node.__fontStyle;
    cellNode.__textDecoration = node.__textDecoration;
    cellNode.__borderStyle = node.__borderStyle;
    cellNode.__padding = node.__padding;
    cellNode.__height = node.__height;
    return cellNode;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      td: (_domNode: HTMLElement) => ({
        conversion: (element: HTMLElement) =>
          convertCustomTableCellElement(element, false),
        priority: 1,
      }),
      th: (_domNode: HTMLElement) => ({
        conversion: (element: HTMLElement) =>
          convertCustomTableCellElement(element, true),
        priority: 1,
      }),
    };
  }

  static importJSON(
    serializedNode: SerializedCustomTableCellNode
  ): CustomTableCellNode {
    const colSpan = serializedNode.colSpan || 1;
    const rowSpan = serializedNode.rowSpan || 1;
    const cellNode = $createCustomTableCellNode(
      serializedNode.headerState,
      colSpan,
      serializedNode.width
    );
    cellNode.__rowSpan = rowSpan;
    cellNode.__backgroundColor = serializedNode.backgroundColor || null;
    cellNode.__textColor = serializedNode.textColor || null;
    cellNode.__fontSize = serializedNode.fontSize || null;
    cellNode.__textAlign = serializedNode.textAlign || null;
    cellNode.__fontWeight = serializedNode.fontWeight || null;
    cellNode.__fontStyle = serializedNode.fontStyle || null;
    cellNode.__textDecoration = serializedNode.textDecoration || null;
    cellNode.__borderStyle = serializedNode.borderStyle || null;
    cellNode.__padding = serializedNode.padding || null;
    cellNode.__height = serializedNode.height || null;
    return cellNode;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const output = super.exportDOM(editor);

    if (output.element && output.element instanceof HTMLTableCellElement) {
      const element = output.element as HTMLTableCellElement;

      // Remove the default inline border style
      element.style.border = "";

      // Set custom width (62px instead of 75px)
      element.style.width = `${this.getWidth() || CUSTOM_COLUMN_WIDTH}px`;

      // Set height if available
      if (this.__height !== null) {
        element.style.height = `${this.__height}px`;
      }

      // Remove vertical-align inline style (let CSS handle it)
      element.style.verticalAlign = "";

      // Keep colSpan and rowSpan attributes
      if (this.__colSpan > 1) {
        element.colSpan = this.__colSpan;
      }
      if (this.__rowSpan > 1) {
        element.rowSpan = this.__rowSpan;
      }

      // Handle background color
      if (this.__backgroundColor !== null) {
        // User has explicitly set a background color
        element.style.backgroundColor = this.__backgroundColor;
      } else {
        // No user background - ensure it's cleared
        element.style.backgroundColor = "";
      }

      // Apply text color
      if (this.__textColor !== null) {
        element.style.color = this.__textColor;
      }

      // Apply font size
      if (this.__fontSize !== null) {
        element.style.fontSize = this.__fontSize;
      }

      // Apply text alignment
      if (this.__textAlign !== null) {
        element.style.textAlign = this.__textAlign;
      } else {
        element.style.textAlign = "start";
      }

      // Apply font weight
      if (this.__fontWeight !== null) {
        element.style.fontWeight = this.__fontWeight;
      }

      // Apply font style
      if (this.__fontStyle !== null) {
        element.style.fontStyle = this.__fontStyle;
      }

      // Apply text decoration
      if (this.__textDecoration !== null) {
        element.style.textDecoration = this.__textDecoration;
      }

      // Apply border style
      if (this.__borderStyle !== null) {
        element.style.border = this.__borderStyle;
      }

      // Apply padding
      if (this.__padding !== null) {
        element.style.padding = this.__padding;
      }

      // Keep the data attribute for Lexical's internal use
      element.setAttribute(
        "data-temporary-table-cell-lexical-key",
        this.getKey()
      );
    }

    return output;
  }

  exportJSON(): SerializedCustomTableCellNode {
    return {
      ...super.exportJSON(),
      type: "custom-tablecell",
      version: 1,
      backgroundColor: this.__backgroundColor,
      textColor: this.__textColor,
      fontSize: this.__fontSize,
      textAlign: this.__textAlign,
      fontWeight: this.__fontWeight,
      fontStyle: this.__fontStyle,
      textDecoration: this.__textDecoration,
      borderStyle: this.__borderStyle,
      padding: this.__padding,
      height: this.__height
    };
  }

  createDOM(config: EditorConfig): HTMLTableCellElement {
    const element = super.createDOM(config);

    // Set the data attribute for Lexical's internal use and event handling
    element.setAttribute(
      "data-temporary-table-cell-lexical-key",
      this.getKey()
    );

    // Apply background color to the created element
    const bgColor = this.getBackgroundColor();
    if (bgColor !== null) {
      element.style.backgroundColor = bgColor;
    }

    // Apply all other styles
    if (this.__textColor !== null) {
      element.style.color = this.__textColor;
    }

    if (this.__fontSize !== null) {
      element.style.fontSize = this.__fontSize;
    }

    if (this.__textAlign !== null) {
      element.style.textAlign = this.__textAlign;
    }

    if (this.__fontWeight !== null) {
      element.style.fontWeight = this.__fontWeight;
    }

    if (this.__fontStyle !== null) {
      element.style.fontStyle = this.__fontStyle;
    }

    if (this.__textDecoration !== null) {
      element.style.textDecoration = this.__textDecoration;
    }

    if (this.__borderStyle !== null) {
      element.style.border = this.__borderStyle;
    }

    if (this.__padding !== null) {
      element.style.padding = this.__padding;
    }

    if (this.__height !== null) {
      element.style.height = `${this.__height}px`;
    }

    return element;
  }

  updateDOM(prevNode: this): boolean {
    const parentNeedsUpdate = super.updateDOM(prevNode);
    const backgroundColorChanged =
      prevNode.getBackgroundColor() !== this.getBackgroundColor();
    const textColorChanged = prevNode.__textColor !== this.__textColor;
    const fontSizeChanged = prevNode.__fontSize !== this.__fontSize;
    const textAlignChanged = prevNode.__textAlign !== this.__textAlign;
    const fontWeightChanged = prevNode.__fontWeight !== this.__fontWeight;
    const fontStyleChanged = prevNode.__fontStyle !== this.__fontStyle;
    const textDecorationChanged =
      prevNode.__textDecoration !== this.__textDecoration;
    const borderStyleChanged = prevNode.__borderStyle !== this.__borderStyle;
    const paddingChanged = prevNode.__padding !== this.__padding;
    const heightChanged = prevNode.__height !== this.__height;

    return (
      parentNeedsUpdate ||
      backgroundColorChanged ||
      textColorChanged ||
      fontSizeChanged ||
      textAlignChanged ||
      fontWeightChanged ||
      fontStyleChanged ||
      textDecorationChanged ||
      borderStyleChanged ||
      paddingChanged ||
      heightChanged
    );
  }

  // Getter and setter methods for all new properties
  getTextColor(): string | null {
    return this.getLatest().__textColor;
  }

  setTextColor(color: string | null): this {
    const writable = this.getWritable();
    writable.__textColor = color;
    return writable;
  }

  getFontSize(): string | null {
    return this.getLatest().__fontSize;
  }

  setFontSize(size: string | null): this {
    const writable = this.getWritable();
    writable.__fontSize = size;
    return writable;
  }

  getTextAlign(): "left" | "center" | "right" | null {
    return this.getLatest().__textAlign;
  }

  setTextAlign(align: "left" | "center" | "right" | null): this {
    const writable = this.getWritable();
    writable.__textAlign = align;
    return writable;
  }

  getFontWeight(): "normal" | "bold" | null {
    return this.getLatest().__fontWeight;
  }

  setFontWeight(weight: "normal" | "bold" | null): this {
    const writable = this.getWritable();
    writable.__fontWeight = weight;
    return writable;
  }

  getFontStyle(): "normal" | "italic" | null {
    return this.getLatest().__fontStyle;
  }

  setFontStyle(style: "normal" | "italic" | null): this {
    const writable = this.getWritable();
    writable.__fontStyle = style;
    return writable;
  }

  getTextDecoration(): "none" | "underline" | null {
    return this.getLatest().__textDecoration;
  }

  setTextDecoration(decoration: "none" | "underline" | null): this {
    const writable = this.getWritable();
    writable.__textDecoration = decoration;
    return writable;
  }

  getBorderStyle(): string | null {
    return this.getLatest().__borderStyle;
  }

  setBorderStyle(border: string | null): this {
    const writable = this.getWritable();
    writable.__borderStyle = border;
    return writable;
  }

  getPadding(): string | null {
    return this.getLatest().__padding;
  }

  setPadding(padding: string | null): this {
    const writable = this.getWritable();
    writable.__padding = padding;
    return writable;
  }

  getHeight(): number | null {
    return this.getLatest().__height;
  }

  setHeight(height: number | null): this {
    const writable = this.getWritable();
    writable.__height = height;
    return writable;
  }
}

/**
 * Cache of CSS class property maps extracted from a document's stylesheets.
 * Keyed by Document so entries are GC'd when the paste document is released.
 * Excel Online puts all cell styles in a <style> block; we extract them once
 * per paste and reuse for every <td> rather than re-scanning per cell.
 */
const docClassStylesCache = new WeakMap<
  Document,
  Map<string, Record<string, string>>
>();

function extractDocClassStyles(
  doc: Document
): Map<string, Record<string, string>> {
  const cached = docClassStylesCache.get(doc);
  if (cached) {return cached;}

  const classStyles = new Map<string, Record<string, string>>();
  try {
    for (let i = 0; i < doc.styleSheets.length; i++) {
      let rules: CSSRuleList | null = null;
      try {
        rules = doc.styleSheets[i].cssRules;
      } catch {
        // Cross-origin stylesheet — skip
      }
      if (!rules) {continue;}
      for (let j = 0; j < rules.length; j++) {
        const rule = rules[j];
        if (!(rule instanceof CSSStyleRule)) {continue;}
        // Split comma-separated selectors, then only accept simple ones:
        // .cls  |  td.cls  |  th.cls
        // Compound (.a.b), descendant (.a .b), child (.a > .b) etc. are
        // skipped to avoid mapping styles onto classes that shouldn't match.
        const parts = rule.selectorText.split(",");
        for (const part of parts) {
          const m = part.trim().match(/^(?:td|th)?\.(\w+)$/i);
          if (!m) {continue;}
          const cls = m[1];
          if (!classStyles.has(cls)) {classStyles.set(cls, {});}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const props = classStyles.get(cls)!;
          const rs = rule.style;
          for (let k = 0; k < rs.length; k++) {
            const prop = rs[k];
            if (!(prop in props)) {props[prop] = rs.getPropertyValue(prop);}
          }
        }
      }
    }
  } catch {
    // Ignore unexpected errors
  }

  docClassStylesCache.set(doc, classStyles);
  return classStyles;
}

/**
 * Excel Online puts cell styles in CSS classes, not inline. This merges class
 * rules with inline styles (inline wins) into a synthetic element so the
 * browser normalizes shorthands (e.g. `background` → `backgroundColor`).
 */
function getEffectiveStyle(element: HTMLElement): CSSStyleDeclaration {
  const { classList, ownerDocument: doc } = element;
  if (classList.length === 0 || !doc?.styleSheets?.length) {
    return element.style;
  }

  const classStyles = extractDocClassStyles(doc);
  const syntheticEl = doc.createElement("div");

  // Apply CSS class rules first (lower precedence).
  for (let i = 0; i < classList.length; i++) {
    const props = classStyles.get(classList[i]);
    if (!props) {continue;}
    for (const [prop, value] of Object.entries(props)) {
      // Let later class rules override earlier ones for the same property.
      syntheticEl.style.setProperty(prop, value);
    }
  }

  // Apply inline styles last (higher precedence — override class rules).
  const inlineStyle = element.style;
  for (let k = 0; k < inlineStyle.length; k++) {
    const prop = inlineStyle[k];
    syntheticEl.style.setProperty(prop, inlineStyle.getPropertyValue(prop));
  }

  return syntheticEl.style;
}

/** Convert a CSS length to pixels. Handles `px` and `pt` units. */
function cssLengthToPx(value: string): number | undefined {
  const match = value.match(/^(\d+(?:\.\d+)?)(px|pt)$/);
  if (!match) {return undefined;}
  const n = parseFloat(match[1]);
  return match[2] === "pt" ? Math.round(n * (96 / 72)) : n;
}

function convertCustomTableCellElement(
  element: HTMLElement,
  isHeader: boolean
): DOMConversionOutput {
  const cellElement = element as HTMLTableCellElement;
  // Use effective style so Excel Online's CSS-class-based styles are captured.
  const style = getEffectiveStyle(element);

  // Width: prefer CSS value (px/pt), fall back to deprecated HTML attribute.
  let width: number | undefined;
  width = cssLengthToPx(style.width);
  if (width === undefined && cellElement.width) {
    const n = parseFloat(String(cellElement.width));
    if (!isNaN(n)) {width = n;}
  }

  const headerState = isHeader
    ? TableCellHeaderStates.ROW
    : TableCellHeaderStates.NO_STATUS;

  // Direct construction — do NOT use $applyNodeReplacement, which would trigger
  // the TextEditor.tsx replace function and discard all custom styles.
  const node = new CustomTableCellNode(headerState, cellElement.colSpan, width);
  node.__rowSpan = cellElement.rowSpan;

  // backgroundColor: browser normalises `background:#xxx` → backgroundColor.
  // Filter transparent values (browser may return "rgba(0,0,0,0)" or "transparent").
  const bgColor = style.backgroundColor || style.background;
  if (bgColor && !/^(transparent|rgba\(0,\s*0,\s*0,\s*0\))$/.test(bgColor)) {
    node.__backgroundColor = bgColor;
  }

  const textColor = style.color;
  if (textColor) {node.__textColor = textColor;}

  const fontSize = style.fontSize;
  if (fontSize) {node.__fontSize = fontSize;}

  const textAlign = style.textAlign;
  if (textAlign === "left" || textAlign === "center" || textAlign === "right") {
    node.__textAlign = textAlign;
  }

  const fontWeight = style.fontWeight;
  if (fontWeight === "bold" || fontWeight === "700") {
    node.__fontWeight = "bold";
  } else if (fontWeight === "normal" || fontWeight === "400") {
    node.__fontWeight = "normal";
  }

  const fontStyle = style.fontStyle;
  if (fontStyle === "italic") {node.__fontStyle = "italic";}
  else if (fontStyle === "normal") {node.__fontStyle = "normal";}

  const rawTextDecoration = style.textDecoration;
  if (rawTextDecoration) {
    if (rawTextDecoration.includes("underline")) {
      node.__textDecoration = "underline";
    } else if (rawTextDecoration === "none") {
      node.__textDecoration = "none";
    }
  }

  // border: check shorthand first, fall back to individual sides
  const border = style.border;
  if (border) {
    node.__borderStyle = border;
  } else {
    const anyBorder =
      style.borderTop ||
      style.borderRight ||
      style.borderBottom ||
      style.borderLeft;
    if (anyBorder) {node.__borderStyle = anyBorder;}
  }

  const padding = style.padding;
  if (padding) {node.__padding = padding;}

  const heightStr = style.height;
  if (heightStr) {
    const h = cssLengthToPx(heightStr) ?? parseFloat(heightStr);
    if (!isNaN(h)) {node.__height = h;}
  }

  // Replicate Lexical's base $convertTableCellNodeElement `after` callback exactly.
  // This wraps inline children in ParagraphNodes and applies cell-level text formatting.
  const textDecorationParts = (rawTextDecoration || "").split(" ");
  const hasBoldFontWeight = fontWeight === "700" || fontWeight === "bold";
  const hasLinethroughTextDecoration = textDecorationParts.includes("line-through");
  const hasItalicFontStyle = fontStyle === "italic";
  const hasUnderlineTextDecoration = textDecorationParts.includes("underline");

  return {
    after: (childLexicalNodes) => {
      const result: LexicalNode[] = [];
      let paragraphNode: ParagraphNode | null = null;

      const removeSingleLineBreakNode = () => {
        if (paragraphNode) {
          const firstChild = paragraphNode.getFirstChild();
          if (
            $isLineBreakNode(firstChild) &&
            paragraphNode.getChildrenSize() === 1
          ) {
            firstChild.remove();
          }
        }
      };

      for (const child of childLexicalNodes) {
        if (
          $isInlineElementOrDecoratorNode(child) ||
          $isTextNode(child) ||
          $isLineBreakNode(child)
        ) {
          if ($isTextNode(child)) {
            if (hasBoldFontWeight) {child.toggleFormat("bold");}
            if (hasLinethroughTextDecoration) {child.toggleFormat("strikethrough");}
            if (hasItalicFontStyle) {child.toggleFormat("italic");}
            if (hasUnderlineTextDecoration) {child.toggleFormat("underline");}
          }
          if (paragraphNode) {
            paragraphNode.append(child);
          } else {
            paragraphNode = $createParagraphNode();
            paragraphNode.append(child);
            result.push(paragraphNode);
          }
        } else {
          result.push(child);
          removeSingleLineBreakNode();
          paragraphNode = null;
        }
      }

      removeSingleLineBreakNode();
      if (result.length === 0) {
        result.push($createParagraphNode());
      }
      return result;
    },
    node,
  };
}

export function $createCustomTableCellNode(
  headerState = 0,
  colSpan = 1,
  width?: number
): CustomTableCellNode {
  return $applyNodeReplacement(
    new CustomTableCellNode(headerState, colSpan, width)
  );
}

export function $isCustomTableCellNode(
  node: LexicalNode | null | undefined
): node is CustomTableCellNode {
  return node instanceof CustomTableCellNode;
}
